import { reportRuleSchema } from '@ghp/domain';
import { z } from 'zod';
import { bearer } from './http.js';
/**
 * Les règles du rapport client (M33) — l'écran « Gestion du rapport PDF » du back-office : quels
 * blocs disparaissent du rapport livré, selon le secteur ou le client. Ouvertes au bureau côté
 * serveur (`@Roles('back_office', 'admin')` sur la classe du contrôleur) ; rien ici ne le rejoue.
 *
 * ⚠️ **Famille à part plutôt qu'un appel sur `inquiries` ou `validation`** : le client suit les
 * chemins de l'API, et une règle n'est le sous-objet d'aucun contrôle — elle vaut pour tous ceux
 * d'un secteur ou d'un client, y compris ceux qui n'existent pas encore. Côté serveur le contrôleur
 * vit dans `inspections/report/`, aux côtés du worker qui produit le PDF ; les deux découpages
 * n'ont pas à coïncider.
 *
 * ⚠️ **Les cibles ne viennent pas d'ici** : les secteurs et les clients se lisent sur `/clients`. Une règle ne porte que des identifiants — les libellés de la liste déroulante
 * appartiennent aux référentiels, et les recopier ici les ferait dériver au premier renommage
 * (M22).
 */
export function createReportRulesEndpoints(http) {
    return {
        /**
         * **Toutes** les règles enregistrées, d'un coup — la table est bornée par le nombre de secteurs
         * et de clients.
         *
         * ⚠️ **Une cible absente de la liste n'a pas de règle, et ce n'est pas une erreur** : son
         * rapport est complet, le cas courant. Il n'y a donc pas de lecture par cible à appeler quand
         * le sélecteur change — l'écran filtre ce qu'il a déjà.
         *
         * ⚠️ **`hiddenBlocks` est ce que le rapport retire vraiment**, pas la ligne brute : un bloc
         * obligatoire n'y figure jamais, le serveur ayant appliqué la même règle que la composition du
         * PDF. Ce que l'écran coche est donc ce que le client recevra.
         */
        list(accessToken) {
            return http.get('/report-rules', z.array(reportRuleSchema), bearer(accessToken));
        },
        /**
         * Écrit la règle d'une cible et rend **la règle écrite** : l'écran réaligne son état sans
         * relire la liste.
         *
         * ⚠️ **Idempotent** : une cible n'a qu'une règle, et un second appel la corrige au lieu d'en
         * créer une seconde. C'est aussi pour cela que « Tout inclure » puis « Enregistrer » s'écrit
         * ici — un `hiddenBlocks` vide, qui laisse la note en place.
         *
         * ⚠️ **Un `400` porte un message à afficher tel quel** : les quatre blocs obligatoires du
         * rapport sont refusés, et la réponse nomme le champ. Un `404` dit que la cible a disparu du
         * référentiel depuis que le sélecteur a été rempli.
         */
        save(accessToken, scope, targetId, rule) {
            return http.putJson(`/report-rules/${scope}/${targetId}`, rule, reportRuleSchema, bearer(accessToken));
        },
        /** Le secteur reprend les réglages de son client (#661) ; rend les règles qui restent. */
        resetLot(accessToken, lotId) {
            return http.deleteJson(`/report-rules/lot/${lotId}`, z.array(reportRuleSchema), bearer(accessToken));
        },
    };
}
//# sourceMappingURL=report-rules.js.map